"use client";

import Cookies from "js-cookie";
import { useAppSelector } from "@/lib/store";
import { useTranslation } from "@/i18n/client";
import { useSearchParams } from "next/navigation";
import { logError } from "@/lib/utils/errors/logger";
import { getProductRating } from "@/lib/services/general";
import { scrollToElement } from "@/lib/utils/browserMethods";
import { setCnCBranchThunk } from "@/lib/store/reducers/cart";
import { updatePlpTitle } from "@/lib/store/reducers/uiState";
import { useLoadingState } from "@/lib/hooks/useLoadingState";
import { useCallback, useEffect, useRef, useState } from "react";
import { updateManualSessionId } from "@/lib/store/reducers/misc";
import { updateShowCheckStock } from "@/lib/store/reducers/globalAssets";
import { getNearestBranches, getProductsStocks } from "@/lib/services/api";
import {
  setToStorage,
  updateMetaInfo,
  branchFormattedDetail,
} from "@/lib/utils/helpers/plp";
import {
  formatEngineOilProducts,
  getBrandFilter,
  getListingPayload,
  getSelectedCat,
  getSKUList,
  getStoredBranch,
  mapItemStocks,
  updateFitmentInfo,
  updateURLParams,
} from "@/lib/utils/listings";
import {
  getSelectedFilters,
  getSortedFilters,
  sortArrayOfObjects,
} from "@/lib/utils/arrayMethods";
import {
  fetchProductFitment,
  getEngineOilProducts,
} from "@/lib/services/productList";
import {
  formatGeocodeError,
  getGeocodeLatLng,
  handleThunkResponse,
  handleToastError,
} from "@/lib/utils/functions";

const useEngineOil = ({
  catId,
  cookieKeys,
  storageKeys,
  componentId,
  userVehicle,
  serviceRes,
  ...rest
}) => {
  const searchParams = useSearchParams();
  const { t } = useTranslation(["product-listing", "common"]);
  const { vehicle } = useAppSelector((state) => state.vehicle);
  const { plpTitle } = useAppSelector((state) => state.uiState);
  const { dispatch, showLoader, hideLoader } = useLoadingState();
  const { manualSessionId, branch: globalBranch } = useAppSelector(
    (state) => state.misc,
  );

  let initial = {
    products: rest.data,
    page: rest.page_number,
    product_total: rest.total,
  };

  const pageFromURL = searchParams.get("page");
  if (pageFromURL && pageFromURL != initial?.page) {
    initial = { page: pageFromURL, products: [], product_total: 0 };
  }
  const skuList = useRef([]); // store product sku list if vehicle selected
  const filters = useRef(null); // store filters list which is applied
  const fitmentData = useRef([]); // store products fitment info
  const allListedSKU = useRef([]); // store all product sku which is available
  const vInfo = useRef(userVehicle); // store selected vehicle and session information
  const isManual = useRef(rest.is_manual); // store manual look up value (from make and models page)[boolean]
  const showSpecific = useRef(!!userVehicle); // store show all or vehicle specific radio input value on 3BO page
  const currentPage = useRef(Number(initial.page)); // store current page value
  const totalProducts = useRef(initial.product_total); // store total_products count
  const additionalCat = useRef([{ id: catId, component_id: componentId }]); // store info for additional categories

  // store branch modal status and show different modal according the status, accepted 0, 1, 2
  const [branchList, setBranchList] = useState([]);
  const [modalStatus, setModalStatus] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedItem, setSelectedItem] = useState({}); // specific product to show in branch modal
  const [showFitment, setShowFitment] = useState(false);
  const [items, setItems] = useState([...initial.products]);
  const [pageLimit, setPageLimit] = useState(rest.page_limit);
  const [sortOrder, setSortOrder] = useState(rest.sort_order);
  const [hasVehicle, setHasVehicle] = useState(!!userVehicle);

  // store all available filter options
  const [filterOpt, setFilterOpt] = useState(
    sortArrayOfObjects(rest.filters_opts, "label"),
  );

  const fetchNearestBranches = useCallback(async ({ coords, limit = 3 }) => {
    const apiRes = await getNearestBranches({
      ...coords,
      skus: allListedSKU.current,
      limit,
    });

    if (
      apiRes?.branches &&
      Array.isArray(apiRes.branches) &&
      apiRes.branches.length > 0
    ) {
      if (limit === 1) return apiRes.branches[0];
      return apiRes.branches;
    }
    return null;
  }, []);

  const updateBranchList = useCallback(
    async (coords) => {
      let status = null;
      const list = await fetchNearestBranches({ coords });
      let branches = [];
      if (list && Array.isArray(list)) {
        status = 2;
        branches = list.map((b) => ({
          ...b,
          selected: globalBranch?.branch_id === b.branch_id,
        }));
      }
      setBranchList(() => branches);
      return status;
    },
    [globalBranch?.branch_id, fetchNearestBranches],
  );

  const updateReviewsAndStocks = useCallback(
    async ({ skuArr, items = [], total_count = 0 }) => {
      let metaInfo = {};
      if (items.length > 0) {
        let mainBranch = globalBranch || {};
        if (!mainBranch?.branch_id) {
          mainBranch = getStoredBranch(storageKeys.selectedBranch);
        }

        const apiRequest = [getProductRating(skuArr)];
        if (mainBranch?.branch_id) {
          apiRequest.push(
            getProductsStocks({
              sku: skuArr,
              branchCode: mainBranch.branch_id,
              hubCode: mainBranch?.is_hub || "",
              hasNDC: mainBranch?.is_NDC || false,
            }),
          );
        }

        metaInfo["selectedBranch"] = mainBranch;

        // Wait for the promises to resolve
        const [revRes, stRes] = await Promise.allSettled(apiRequest);
        const reviewArr = revRes.status === "fulfilled" ? revRes.value : [];
        if (revRes.status === "rejected") {
          logError({
            info: { skuArr },
            error: revRes.reason,
            message: "getProductRating",
          });
        }

        const itemStocks = stRes?.status === "fulfilled" ? stRes.value : {};
        if (stRes?.status === "rejected") {
          logError({
            info: { mainBranch, skuArr },
            error: stRes.reason,
            message: "getProductsStocks",
          });
        }

        skuArr.forEach((sku) => {
          const review = reviewArr?.find((r) => r.sku === sku);
          const ndcStock = itemStocks?.ndc?.find((i) => i.sku === sku);
          const hubStock = itemStocks?.hub?.find((i) => i.sku === sku);
          const branchStock = itemStocks?.products?.find((i) => i.sku === sku);

          metaInfo[sku] = {
            branch: mainBranch,
            review: review || null,
            hub_stock: hubStock?.freeStock || 0,
            ndc_stock: ndcStock?.freeStock || 0,
            branch_stock: branchStock?.freeStock || 0,
          };
        });
      }

      setItems(() =>
        formatEngineOilProducts(items, metaInfo, rest.config_icons),
      );
      totalProducts.current = total_count;
    },
    [globalBranch?.branch_id],
  );

  const fetchCatProducts = useCallback(
    async (params = {}) => {
      showLoader();
      dispatch(updateShowCheckStock(false));
      const {
        limitOpt,
        filterOpts,
        sortProductBy,
        pushState = true,
        resetBrand = false,
        replaceHistory = false,
      } = params;

      try {
        const queryParam = new URL(window.location.href).searchParams;
        const autoApplyCoupon = queryParam.has("auto_apply_coupon")
          ? queryParam.get("auto_apply_coupon")
          : null;

        const { payload, manualLookUp, continueFetch } = getListingPayload({
          queryParam,
          cookieKeys,
          componentId,
          manualSessionId,
          skuList: [],
          initial: { is3BO: true },
          isManual: isManual.current,
          currentVehicle: vInfo.current,
          showSpecific: showSpecific.current,
          sortOpt: sortProductBy || sortOrder,
          specialCoupon: autoApplyCoupon || null,
        });

        if (!continueFetch) {
          setItems([]);
          setFilterOpt([]);
          updateMetaInfo(1);
          currentPage.current = 1;
          totalProducts.current = 0;
          hideLoader();
          logError({
            isWarning: true,
            info: { ...payload, manualLookUp },
            message: "component id does not exist for this category",
          });
          return;
        }

        isManual.current = manualLookUp;
        setIsFetching(() => true);
        const filtersObj = updateURLParams({
          pushState,
          hasVehicle,
          resetBrand,
          replaceHistory,
          params: searchParams,
          page: currentPage.current,
          isManual: isManual.current,
          selectedFilters: filterOpts || filters.current,
        });

        const { catIdArr, compIdArr } = getSelectedCat({
          catId,
          componentId,
          initialCat: rest.additional,
          additional: additionalCat.current,
        });

        if (payload.vFilter) {
          payload.vFilter.component_id = compIdArr.join(",");
        }

        // pass filters for engine oil products
        const { data, errors } = await getEngineOilProducts({
          catIdArr,
          skuList: payload.skuList,
          vFilter: payload.vFilter,
          sortOrder: payload.sortOpt,
          currentPage: currentPage.current,
          specialCoupon: payload.specialCoupon,
          productPageSize: limitOpt || pageLimit,
          queryParams: filtersObj,
        });

        if (errors) {
          let errMsg = errors;
          if (Array.isArray(errors) && errors.length > 0) {
            errMsg = errors[0]?.message || "";
          }

          if (payload.vFilter && errMsg === serviceRes.unavailable) {
            handleToastError(t("messages.service_unavailable"));
          } else if (errMsg) {
            logError({
              error: errors,
              message: "engine oil products list",
              info: {
                catId,
                ...payload,
                apiError: errMsg,
                page: currentPage.current,
              },
            });
          }

          setItems(() => []);
          setFilterOpt(() => []);
          setIsFetching(() => false);
          totalProducts.current = 0;
        } else if (
          data?.products?.items &&
          Array.isArray(data.products.items)
        ) {
          const { itemSku, vrmSkuList } = getSKUList(
            data.products.items,
            skuList.current,
            payload.sessionInfo,
            true,
          );
          allListedSKU.current = itemSku;
          if (payload.sessionInfo && vrmSkuList.length > 0) {
            skuList.current = [...skuList.current, ...vrmSkuList];
          }

          const { options, current } = getSortedFilters(
            filters.current,
            data.products?.aggregations,
          );

          setFilterOpt(options);
          filters.current = current;
          await updateReviewsAndStocks({
            ...data.products,
            skuArr: allListedSKU.current,
          });
          setBranchList(() => []);
          setIsFetching(() => false);
        }
      } catch (error) {
        console.error(error);
      }

      hideLoader();
      dispatch(updateShowCheckStock(true));
    },
    [
      dispatch,
      pageLimit,
      sortOrder,
      hasVehicle,
      componentId,
      searchParams,
      manualSessionId,
      updateReviewsAndStocks,
    ],
  );

  const handleVehicleSpecific = useCallback(
    (e) => {
      const { id } = e.target;
      const isShowAll = id === "show_all";
      if (isShowAll) {
        showLoader();
        filters.current = [];
        currentPage.current = 1;
        isManual.current = false;
        showSpecific.current = false;
        updateMetaInfo(1);
        dispatch(updatePlpTitle(""));
        dispatch(updateManualSessionId(null));
        fetchCatProducts({ filterOpts: [], resetBrand: true });
        hideLoader();
        return;
      } else if (!hasVehicle) {
        document
          .querySelector("#current-vehicle")
          .classList.add("border-danger");
        scrollToElement("current-vehicle");
        const showAll = document.querySelector("#show_all");
        e.target.checked = false;
        showAll.checked = true;
        return;
      }

      updateMetaInfo(1);
      filters.current = [];
      currentPage.current = 1;
      showSpecific.current = !isShowAll;
      fetchCatProducts({ filterOpts: [] });
    },
    [hasVehicle, manualSessionId, fetchCatProducts],
  );

  const handlePageChange = useCallback(
    (pageNo) => {
      setBranchList(() => []);
      currentPage.current = pageNo;
      fetchCatProducts();
    },
    [fetchCatProducts],
  );

  const handleSortingSelection = useCallback(
    (sorting) => {
      currentPage.current = 1;
      updateMetaInfo(1);
      setSortOrder(sorting);
      setBranchList(() => []);
      fetchCatProducts({ sortProductBy: sorting });
    },
    [fetchCatProducts],
  );

  const checkPlpTitle = (filterArr) => {
    const qualityItems = filterArr.filter(
      (item) => item.label.toUpperCase() == plpTitle?.toUpperCase(),
    );
    const count = filterArr.filter((item) => item.code == "quality");
    if (
      !qualityItems?.length ||
      count?.length > 1 ||
      (qualityItems?.length == 0 && count?.length == 0)
    ) {
      dispatch(updatePlpTitle(""));
    }
  };

  const handleFilterSelection = useCallback(
    (filterArr, isReset = false) => {
      currentPage.current = 1;
      checkPlpTitle(filterArr);
      updateMetaInfo(1);
      filters.current = filterArr;
      if (isReset) {
        additionalCat.current = [{ id: catId, component_id: componentId }];
      }
      fetchCatProducts({ filterOpts: filterArr, resetBrand: isReset });
    },
    [fetchCatProducts],
  );

  const handlePageSelection = useCallback(
    (limit) => {
      currentPage.current = 1;
      updateMetaInfo(1);
      setPageLimit(limit);
      fetchCatProducts({ limitOpt: limit });
    },
    [fetchCatProducts],
  );

  const handleSizeSelection = useCallback((selectedId) => {
    const getUpdatedItem = (product) => {
      let item = { ...product };
      const hasItem = product.items?.find((i) => i.id === selectedId);
      if (hasItem) {
        item.selected = selectedId;
      }
      return item;
    };

    setItems((prev) => prev.map((p) => getUpdatedItem(p)));
  }, []);

  const toggleFitmentModal = useCallback(
    async ({ sku = null, fitment = [] }) => {
      fitmentData.current = [];
      const show = !showFitment;
      if (show) {
        fitmentData.current = fitment;
        if (!fitment?.length) {
          showLoader();
          const { data } = await fetchProductFitment(sku);
          fitmentData.current = data?.productFitmentInfo || fitment_info;
          setItems((prev) =>
            updateFitmentInfo(sku, prev, fitmentData.current, "items"),
          );
        }
      }
      hideLoader();
      setShowFitment(show);
      if (show) {
        document.body.classList.add("body-fixed");
      } else {
        document.body.classList.remove("body-fixed");
      }
    },
    [showFitment],
  );

  const toggleBranchModal = useCallback(
    async (item = false) => {
      let modalStatus = 0;
      if (item) {
        const hasBranches = branchList.length > 0;
        modalStatus = hasBranches ? 2 : 1;
        if (
          globalBranch?.branch_id &&
          globalBranch?.latitude &&
          globalBranch?.longitude &&
          !hasBranches
        ) {
          showLoader();
          const status = await updateBranchList({
            lat: Number(globalBranch.latitude),
            lng: Number(globalBranch.longitude),
          });
          if (status) modalStatus = status;
          hideLoader();
        }
      }
      setSelectedItem(item);
      setModalStatus(modalStatus);
    },
    [
      dispatch,
      branchList.length,
      fetchNearestBranches,
      globalBranch?.branch_id,
    ],
  );

  const handleCheckStock = useCallback(
    async (e) => {
      try {
        showLoader();
        const coords = await getGeocodeLatLng(e?.postCode);
        await updateBranchList(coords);
        setSelectedItem((prev) => ({ ...prev, query_text: e?.postCode }));
        setModalStatus(2);
        hideLoader();
      } catch (error) {
        hideLoader();
        handleToastError(formatGeocodeError(error?.code));
        console.error(error);
      }
    },
    [dispatch, updateBranchList],
  );

  const handleBranchChange = useCallback((branchId) => {
    setBranchList((prev) =>
      prev.map((b) => ({ ...b, selected: branchId === b.branch_id })),
    );
  }, []);

  const handleBranchSelection = useCallback(async () => {
    let branch = branchList.find((b) => b.selected);
    if (!branch) {
      handleToastError(t("messages.error.invalid_branch"));
      return;
    }

    setModalStatus(0);
    setSelectedItem(false);
    setToStorage(storageKeys.selectedBranch, branch);
    setItems((prev) => mapItemStocks(prev, branch, "items"));
    dispatch(setCnCBranchThunk({ branch: branchFormattedDetail(branch) })).then(
      (res) => handleThunkResponse(res),
    );
  }, [t, dispatch, branchList]);

  const handleToggleCategory = useCallback(
    (item) => {
      const categories = additionalCat.current;
      const index = categories.findIndex(({ id }) => item.id === id);
      if (index >= 0) {
        additionalCat.current = categories.filter(({ id }) => item.id !== id);
      } else {
        additionalCat.current = [...categories, item];
      }
      fetchCatProducts();
    },
    [additionalCat],
  );

  const fetchProductsOnPageLoad = useCallback(
    async ({ sParams }) => {
      let tempFilters;
      if (!filters.current) {
        filters.current = getSelectedFilters(rest.filters_opts, tempFilters);
        tempFilters = filters.current;
      } else {
        filters.current = [];
      }

      if (sParams.has("brand")) {
        const brand = sParams.get("brand");
        const hasFilter = filters.current.find(
          (c) => c.label.toLowerCase() === brand.toLowerCase(),
        );

        if (!hasFilter) {
          const brandOpt = await getBrandFilter({
            brand,
            filterOptions: filterOpt,
          });

          if (brandOpt?.value) {
            tempFilters = [...filters.current, brandOpt];
            filters.current = tempFilters;
          }
        }
      }

      if (Cookies.get(cookieKeys.new_vehicle)) {
        currentPage.current = 1;
        updateMetaInfo(1);
        Cookies.remove(cookieKeys.new_vehicle);
      }

      isManual.current = false;
      fetchCatProducts({ filterOpts: tempFilters, replaceHistory: true });
    },
    [hasVehicle, filterOpt, fetchCatProducts],
  );

  // handling vehicle set on store
  useEffect(() => {
    setIsFetching(true);
    const hasCookie = Cookies.get(cookieKeys.selectedVehicle);
    let stopProcessing = !!hasCookie;
    if (vehicle) {
      stopProcessing =
        vInfo.current?.session?.sessionId === vehicle?.session?.sessionId;
    }

    if (!stopProcessing) {
      vInfo.current = vehicle;
      setHasVehicle(!!vehicle);
      return;
    }
    setIsFetching(false);
  }, [vehicle]);

  // handling product fetch when page load, vehicle or page number change
  useEffect(() => {
    const vCurrent = vInfo.current; // get current vehicle
    const hasNoVehicle = !vCurrent?.session?.sessionId;
    const sParams = new URL(window.location.href).searchParams;

    showSpecific.current = !hasNoVehicle;
    isManual.current = sParams.has("make") && sParams.has("model");

    // set show specific in case of manual selection
    if (!showSpecific.current && isManual.current) {
      showSpecific.current = true;
    }

    let noFetching = false;
    if (showSpecific.current) {
      noFetching = !componentId;
      if (!noFetching && hasNoVehicle) {
        noFetching = isManual.current;
      }
    }

    if (noFetching) {
      setIsFetching(false);
      setItems(() => []);
      setFilterOpt(() => []);
      currentPage.current = 1;
      updateMetaInfo(1);
      totalProducts.current = 0;
      return;
    }

    fetchProductsOnPageLoad({ sParams });
  }, [hasVehicle]);

  // handling browser back and forward
  useEffect(() => {
    const handleBrowserBackAndForward = () => {
      const sParams = new URLSearchParams(window.location.search);
      setBranchList(() => []);
      currentPage.current = sParams.has("page")
        ? Number(sParams.get("page"))
        : 1;
      fetchCatProducts({ pushState: false });
    };

    window.addEventListener("popstate", handleBrowserBackAndForward);
    return () => {
      window.removeEventListener("popstate", handleBrowserBackAndForward);
    };
  }, []);

  return {
    data: {
      items,
      filterOpt,
      sortOrder,
      pageLimit,
      branchList,
      isFetching,
      modalStatus,
      showFitment,
      globalBranch,
      searchParams,
      selectedItem,
      vehicle: vInfo.current,
      skuList: skuList.current,
      filters: filters?.current || [],
      currentPage: currentPage.current,
      fitmentInfo: fitmentData.current,
      showSpecific: showSpecific.current,
      additionalCat: additionalCat.current,
      totalProducts: totalProducts.current,
    },
    methods: {
      t,
      dispatch,
      handlePageChange,
      handleCheckStock,
      toggleBranchModal,
      toggleFitmentModal,
      handleBranchChange,
      handleSizeSelection,
      handlePageSelection,
      handleToggleCategory,
      handleBranchSelection,
      handleVehicleSpecific,
      handleFilterSelection,
      handleSortingSelection,
    },
  };
};

export default useEngineOil;
